import { cohortsKeys } from '@/hooks/queries/cohorts';
import { useMutation, useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { StandardResponse } from 'Src/types/common';
import UserService from '../../../services/user-service';
import type {
  BulkCohortAssignmentPayload,
  CreateUserData,
  HardDeleteUserPayload,
  HardDeleteUserResponse,
  HardDeleteImpactResponse,
  SendWelcomeEmailPayload,
  SendWelcomeEmailResponse,
  TenantUser,
  UpdateUserCohortsPayload,
  UpdateUserData,
  User,
} from '../../../types/user';
import { getErrorMessage } from '@/utils/common';

function removeUserFromUsersListCache(queryClient: QueryClient, userId: number) {
  queryClient.setQueriesData<StandardResponse<TenantUser>>(
    { queryKey: ['users'] },
    (cached) => {
      if (!cached?.results) return cached;

      const results = cached.results.filter(
        (tenantUser) => tenantUser.user.id !== userId
      );

      if (results.length === cached.results.length) {
        return cached;
      }

      return {
        ...cached,
        count: Math.max(0, cached.count - 1),
        results,
      };
    }
  );
}

// Get every user on the tenant (all pages)
export const useGetUsers = (tenantId: number | null) => {
  return useQuery<StandardResponse<TenantUser>>({
    queryKey: ['users', tenantId],
    queryFn: () => UserService.getAllTenantUsers(tenantId!),
    enabled: !!tenantId,
  });
};

// Get single user
export const useGetUser = (userId: string | undefined) => {
  return useQuery<User>({
    queryKey: ['user', userId],
    queryFn: async () => {
      const response = await UserService.getUsersById(parseInt(userId!));
      return response as User;
    },
    enabled: !!userId,
  });
};

// Create or update user
export const useCreateOrUpdateUser = (
  options: {
    onSuccess?: () => void;
    successMessage?: string;
    onError?: (error: any) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: number;
      data: { org_id: string; user: CreateUserData | UpdateUserData };
    }) => {
      const ordId = data.org_id;
      if (id) {
        return UserService.updateOrgUser(parseInt(ordId), id, data);
      }
      return UserService.createOrgUser(parseInt(ordId), data);
    },
    onSuccess: (_, variables) => {
      const message =
        options.successMessage ??
        (variables.id
          ? 'User updated successfully'
          : 'User created successfully');
      toast.success(message);

      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ['user', variables.id.toString()],
        });
      }
      options.onSuccess?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage((error as any).response?.data ?? error));
      options.onError?.(error);
    },
  });
};

export const useUpdateUserCohorts = (
  options: {
    onSuccess?: () => void;
    successMessage?: string;
    onError?: (error: any) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: number;
      data: UpdateUserCohortsPayload;
    }) => UserService.updateUser(userId, data),
    onSuccess: (_, variables) => {
      toast.success(options.successMessage ?? 'User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({
        queryKey: ['user', variables.userId.toString()],
      });
      queryClient.invalidateQueries({ queryKey: cohortsKeys.my() });
      options.onSuccess?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage((error as any).response?.data ?? error));
      options.onError?.(error);
    },
  });
};

export const useBulkAssignUserCohorts = (
  options: {
    onSuccess?: () => void;
    successMessage?: string;
    onError?: (error: any) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkCohortAssignmentPayload) =>
      UserService.bulkAssignUserCohorts(payload),
    onSuccess: () => {
      toast.success(
        options.successMessage ?? 'Cohorts assigned successfully'
      );
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: cohortsKeys.my() });
      options.onSuccess?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage((error as any).response?.data ?? error));
      options.onError?.(error);
    },
  });
};

function formatWelcomeEmailToast(response: SendWelcomeEmailResponse): string {
  const sent = `${response.sent_count} invitation${
    response.sent_count === 1 ? '' : 's'
  } sent`;

  if (response.failed_count > 0) {
    return `${sent}, ${response.failed_count} failed`;
  }

  return sent;
}

export const useSendWelcomeEmails = (
  options: {
    onSuccess?: (response: SendWelcomeEmailResponse) => void;
    onError?: (error: unknown) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SendWelcomeEmailPayload) =>
      UserService.sendWelcomeEmails(payload),
    onSuccess: (response) => {
      const message = formatWelcomeEmailToast(response);

      if (response.failed_count > 0) {
        toast.error(message);
      } else {
        toast.success(message);
      }

      queryClient.invalidateQueries({ queryKey: ['users'] });
      options.onSuccess?.(response);
    },
    onError: (error: unknown) => {
      const data = (error as { response?: { data?: unknown } })?.response?.data;
      toast.error(getErrorMessage(data ?? error));
      options.onError?.(error);
    },
  });
};

export const useHardDeleteImpact = (
  userId: number | null,
  enabled: boolean
) => {
  return useQuery<HardDeleteImpactResponse>({
    queryKey: ['users', userId, 'hard-delete-impact'],
    queryFn: () => UserService.getHardDeleteImpact(userId!),
    enabled: enabled && userId != null,
    retry: false,
  });
};

export const useExecuteHardDelete = (
  options: {
    onSuccess?: (response: HardDeleteUserResponse) => void;
    onError?: (error: unknown) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: number;
      payload: HardDeleteUserPayload;
    }) => UserService.executeHardDelete(userId, payload),
    onSuccess: (response, variables) => {
      removeUserFromUsersListCache(queryClient, variables.userId);
      options.onSuccess?.(response);
    },
    onError: (error) => {
      toast.error(getErrorMessage((error as any).response?.data ?? error));
      options.onError?.(error);
    },
  });
};

export const useRemoveUserFromTenant = (
  options: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => UserService.removeUserFromTenant(userId),
    onSuccess: (_response, userId) => {
      toast.success('User removed from this tenant.');
      removeUserFromUsersListCache(queryClient, userId);
      options.onSuccess?.();
    },
    onError: (error) => {
      const status = (error as any)?.response?.status;
      if (status === 404) {
        toast.error('User not found in the active tenant.');
      } else {
        toast.error(getErrorMessage((error as any).response?.data ?? error));
      }
      options.onError?.(error);
    },
  });
};

// Deactivate user
export const useDeactivateUser = (
  options: {
    onSuccess?: () => void;
    onError?: (error: any) => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    // mutationFn: (userId: number) => UserService.deactivateUser(userId),
    onSuccess: () => {
      toast.success('User deactivated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      options.onSuccess?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
      options.onError?.(error);
    },
  });
};

// Delete user
export const useDeleteUser = (
  options: {
    onSuccess?: () => void;
  } = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: number; userId: number }) =>
      UserService.deleteOrgUser(orgId, userId),
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      options.onSuccess?.();
    },
  });
};

// Get all users
export const useGetUserOrgs = () => {
  return useQuery({
    queryKey: ['user-orgs'],
    queryFn: async () => {
      const response = await UserService.getUserOrgs();
      return response;
    },
    enabled: false,
  });
};

export const useGetTenants = ({ enabled }: { enabled: boolean }) => {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const response = await UserService.getTenants();
      return response;
    },
    enabled: enabled,
  });
};
